// Estimates — GET /api/estimates (?archived=1 for the archive) as a DataTable
// with estimate_status pills. Every state action lives on the detail screen.
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, Pill } from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get } from '../../shared/http';
import { fmtMoney, fmtDateTime } from '../shared/format';
import { ESTIMATE_STATUS, estimatePill } from '../shared/status';
import { PageHeader, Refused, LoadFailed, Loading } from '../shared/Page';
import { clientKey, clientOptions } from '../shared/jobsByClient';

/**
 * `clientId` is a client id, 'none' for estimates with no client record, or
 * '' for any — the same `?client=` the Invoices and Jobs lists read, keyed on
 * the link rather than the typed name (owner, 2026-09-26: filter by client).
 */
export function filterEstimates(list, { status, search, clientId }) {
  const s = (search || '').trim().toLowerCase();
  return (list || []).filter((e) => {
    if (status && status !== 'all' && (e.estimate_status || 'draft') !== status) return false;
    if (clientId && clientKey(e) !== clientId) return false;
    if (s) {
      const hay = [e.invoice_number, e.client_name, e.title, e.description].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
}

export default function Estimates() {
  const canRead = useCan('estimates', 'read');
  const canCreate = useCan('estimates', 'create');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'all';
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);

  const q = useQuery({
    queryKey: ['office', 'estimates', archived ? 'archived' : 'active'],
    queryFn: () => get(archived ? '/estimates?archived=1' : '/estimates'),
    enabled: canRead,
  });
  // Options from the estimates on screen, A–Z; a `?client=` the list cannot
  // offer is dropped rather than painting "Any client" over an empty list.
  const clients = useMemo(() => clientOptions(q.data), [q.data]);
  const anyNoClient = useMemo(() => (q.data || []).some((e) => clientKey(e) === 'none'), [q.data]);
  const rawClient = params.get('client') || '';
  const clientId = (rawClient === 'none' && anyNoClient) || clients.some((c) => c.id === rawClient) ? rawClient : '';
  const rows = useMemo(() => filterEstimates(q.data, { status, search, clientId }), [q.data, status, search, clientId]);

  if (!canRead) return <Refused what="estimates" />;

  // Same card shape as Invoices, and deliberately so: an estimate and an
  // invoice are the same question asked at two times, and a person moving
  // between the two screens on a phone should not have to re-learn where the
  // number, the client, the state and the amount are. Status is the badge
  // (draft / sent / accepted / declined is the whole reason this list is
  // opened); the job count is table-only.
  const columns = [
    { key: 'invoice_number', label: 'Estimate', role: 'title', render: (r) => (<><strong>{r.invoice_number}</strong>{r.title ? <span className="of-cell__sub">{r.title}</span> : null}</>) },
    { key: 'client_name', label: 'Client', role: 'subtitle', render: (r) => r.client_name || '—' },
    { key: 'jobs', label: 'Jobs', role: 'hidden', render: (r) => (r.job_count ? `${r.job_count} job${Number(r.job_count) === 1 ? '' : 's'}` : '—') },
    { key: 'updated_at', label: 'Updated', role: 'fact', render: (r) => fmtDateTime(r.updated_at || r.created_at) },
    { key: 'status', label: 'Status', role: 'badge', render: (r) => { const p = estimatePill(r); return (<><Pill tone={p.tone} data-status={r.estimate_status}>{p.label}</Pill>{r.converted_invoice_number ? <span className="of-cell__sub">→ {r.converted_invoice_number}</span> : null}</>); } },
    { key: 'total', label: 'Amount', align: 'right', role: 'fact', render: (r) => fmtMoney(r.total) },
  ];

  const setStatus = (v) => { const next = new URLSearchParams(params); if (v === 'all') next.delete('status'); else next.set('status', v); setParams(next, { replace: true }); };
  const setClient = (v) => { const next = new URLSearchParams(params); if (!v) next.delete('client'); else next.set('client', v); setParams(next, { replace: true }); };

  return (
    <>
      <PageHeader
        title="Estimates"
        meta={q.data ? `${rows.length} of ${q.data.length}${archived ? ' archived' : ''}` : null}
        actions={canCreate ? <FwButton variant="primary" icon="plus" onClick={() => navigate('/estimates/new')}>New estimate</FwButton> : null}
      />
      <div className="of-filters">
        <FwField label="Status" htmlFor="of-est-status">
          <FwSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            {Object.entries(ESTIMATE_STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Client" htmlFor="of-est-client">
          <FwSelect id="of-est-client" aria-label="Filter by client" value={clientId} onChange={(e) => setClient(e.target.value)}>
            <option value="">Any client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            {/* "No client record", as on Invoices: an estimate can carry a
                typed client NAME with no client record behind it (#711 review). */}
            {anyNoClient ? <option value="none">No client record</option> : null}
          </FwSelect>
        </FwField>
        <FwField label="Search" htmlFor="of-est-search" className="of-filters__search">
          <FwInput type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Number, client or title" />
        </FwField>
        <button type="button" className="of-toggle" aria-pressed={archived ? 'true' : 'false'} onClick={() => setArchived((v) => !v)}>Show archived</button>
      </div>
      {q.isPending ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="estimates" /> : null}
      {q.data ? (
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={(r) => navigate(`/estimates/${r.id}`)}
          empty={q.data.length ? 'No estimates match these filters.' : (archived ? 'Nothing archived.' : 'No estimates yet.')}
          caption="Estimates"
        />
      ) : null}
    </>
  );
}
